import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useGetConnections } from '@utils/api/connections/use-get-connections.tsx';
import { useGetPublishTargets } from '@utils/api/publishing/use-get-publish-targets.tsx';
import { useStartPublishJob } from '@utils/api/publishing/use-start-publish-job.tsx';
import { useGetPublishJob } from '@utils/api/publishing/use-get-publish-job.tsx';
import { useCancelPublishJob } from '@utils/api/publishing/use-cancel-publish-job.tsx';
import { useGetDocumentPublishHistory } from '@utils/api/publishing/use-get-document-publish-history.tsx';
import {
  forgetPublishJob,
  recallPublishJob,
  rememberPublishJob
} from '@utils/localStorage/publish-job-storage.ts';
import { isSameTarget } from '@utils/types/publishing/publish.ts';
import type {
  PublishHistoryEntry,
  PublishPayload,
  PublishTarget,
  StartPublishJobPayload
} from '@utils/types/publishing/publish.ts';
import { hasYouTubeUploadScope } from '@utils/connections/upload-scope.ts';
import { buildInitialState, type DialogState } from './types';
import { translatePublishStartError } from './publish-start-error';

interface Args {
  isOpen: boolean;
  sessionId: string;
  documentId: string;
  defaultPayloadKind: 'captions' | 'transcript';
  /** Caption languages the document actually has. */
  availableCaptionLanguages: string[];
}

export function usePublishDialog({
  isOpen,
  sessionId,
  documentId,
  defaultPayloadKind,
  availableCaptionLanguages
}: Args) {
  const { t } = useTranslation();

  const [state, setState] = useState<DialogState>(() =>
    buildInitialState(defaultPayloadKind, availableCaptionLanguages)
  );

  // Re-seed when the dialog opens — same reasoning as the connect
  // wizard, parent keeps us mounted so useState initializer stays stale.
  const wasOpenRef = useRef(isOpen);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setState(
        buildInitialState(defaultPayloadKind, availableCaptionLanguages)
      );
    }
    wasOpenRef.current = isOpen;
  }, [availableCaptionLanguages, defaultPayloadKind, isOpen]);

  const { data: connections = [], isLoading: connectionsLoading } =
    useGetConnections(isOpen);
  const { data: history } = useGetDocumentPublishHistory(
    sessionId,
    documentId,
    isOpen
  );

  const selectedConnection = useMemo(
    () => connections.find(c => c.id === state.connectionId) ?? null,
    [connections, state.connectionId]
  );

  // Same question the Connections row asks, so the scope lives in one
  // place rather than being spelled out in both.
  const selectedConnectionHasUploadScope = useMemo(
    () => hasYouTubeUploadScope(selectedConnection),
    [selectedConnection]
  );

  const youtubeUploadsEnabled = !!selectedConnection?.video_uploads_enabled;
  const publicPrivacyAvailable = !!selectedConnection?.public_privacy_available;

  const [targetQuery, setTargetQuery] = useState('');
  // Debounce the query so fast typing doesn't flood the backend. Keeps
  // targetQuery synchronous for the input and only moves debouncedQuery
  // ~250ms later which is what the fetcher reads.
  const [debouncedQuery, setDebouncedQuery] = useState('');
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(targetQuery), 250);
    return () => clearTimeout(handle);
  }, [targetQuery]);

  const {
    data: targetsData,
    isLoading: targetsLoading,
    hasNextPage: targetsHasNextPage,
    isFetchingNextPage: targetsIsFetchingNextPage,
    fetchNextPage: targetsFetchNextPage
  } = useGetPublishTargets({
    connectionId:
      isOpen && state.view !== 'choose-connection' ? state.connectionId : null,
    query: debouncedQuery
  });

  const flattenedTargets = useMemo(
    () => (targetsData?.pages ?? []).flatMap(p => p.result),
    [targetsData]
  );

  const { mutateAsync: startPublishJob } = useStartPublishJob();
  const { mutateAsync: cancelPublishJob, isLoading: isCancellingJob } =
    useCancelPublishJob();

  // When a job id is in state (either freshly started or recalled from
  // localStorage), poll it. The poller is driven by this id, not by the
  // dialog's view — a cold reload still picks it back up.
  const [activeJobId, setActiveJobId] = useState<string | null>(() =>
    recallPublishJob(sessionId, documentId)
  );
  const { data: polledJob } = useGetPublishJob(activeJobId);

  // Reflect polled progress into dialog state.
  useEffect(() => {
    if (!polledJob) return;
    setState(prev => ({ ...prev, activeJob: polledJob, view: 'in-flight' }));
    if (
      polledJob.status === 'succeeded' ||
      polledJob.status === 'failed' ||
      polledJob.status === 'cancelled'
    ) {
      forgetPublishJob(sessionId, documentId);
    }
  }, [documentId, polledJob, sessionId]);

  const goTo = useCallback(
    (view: DialogState['view']) => setState(prev => ({ ...prev, view })),
    []
  );

  const chooseConnection = useCallback((connectionId: string) => {
    setState(prev => ({ ...prev, connectionId, target: null }));
  }, []);

  const pickTarget = useCallback(
    (target: PublishTarget, agendaItemId?: string) => {
      setState(prev => ({
        ...prev,
        target,
        agendaItemId: agendaItemId ?? null
      }));
    },
    []
  );

  const setPayload = useCallback((payload: PublishPayload) => {
    setState(prev => ({ ...prev, payload }));
  }, []);

  const priorAttempt: PublishHistoryEntry | undefined = useMemo(() => {
    if (!state.target || !selectedConnection) return undefined;
    const list = history ?? [];
    return list.find(entry =>
      isSameTarget(entry, {
        platform: selectedConnection.platform,
        connection_id: selectedConnection.id,
        target: state.target as PublishTarget,
        agenda_item_id: state.agendaItemId ?? undefined
      })
    );
  }, [history, selectedConnection, state.agendaItemId, state.target]);

  const confirmPublish = useCallback(async () => {
    if (!selectedConnection || !state.target) return;
    // Flatten the sub-target picked for iBabs into target_ref so the
    // backend's opaque dict carries everything the connector reads
    // without a sibling field (api-scriptix#4100).
    const target_ref: Record<string, unknown> = { ...state.target };
    if (state.agendaItemId) target_ref.agenda_item_id = state.agendaItemId;
    const payload: StartPublishJobPayload = {
      connection_id: selectedConnection.id,
      target_ref,
      payload: state.payload
    };
    try {
      const job = await startPublishJob({
        sessionId,
        documentId,
        payload
      });
      rememberPublishJob(sessionId, documentId, job.id);
      setActiveJobId(job.id);
      setState(prev => ({ ...prev, activeJob: job, view: 'in-flight' }));
    } catch (err) {
      toast.error(translatePublishStartError(err, t));
    }
  }, [
    documentId,
    selectedConnection,
    sessionId,
    startPublishJob,
    state.agendaItemId,
    state.payload,
    state.target,
    t
  ]);

  const retryPublish = useCallback(() => {
    forgetPublishJob(sessionId, documentId);
    setActiveJobId(null);
    setState(prev => ({ ...prev, activeJob: null, view: 'confirm' }));
  }, [documentId, sessionId]);

  const pickDifferentTarget = useCallback(() => {
    forgetPublishJob(sessionId, documentId);
    setActiveJobId(null);
    setState(prev => ({
      ...prev,
      activeJob: null,
      target: null,
      agendaItemId: null,
      view: 'choose-target'
    }));
  }, [documentId, sessionId]);

  const cancelActiveJob = useCallback(async () => {
    if (!activeJobId) return;
    try {
      await cancelPublishJob(activeJobId);
      // Poller will reflect the cancelled status; forget on terminal.
    } catch (err) {
      const detail =
        (err as { response?: { data?: { error_description?: string } } })
          ?.response?.data?.error_description ?? t('publish-cancel-failed');
      toast.error(detail);
    }
  }, [activeJobId, cancelPublishJob, t]);

  const canAdvance = useMemo(() => {
    switch (state.view) {
      case 'choose-connection':
        return !!state.connectionId;
      case 'choose-target': {
        if (!state.target) return false;
        if (state.target.kind === 'ibabs_meeting') return !!state.agendaItemId;
        if (state.target.kind === 'youtube_new_video') {
          // Reconnect gate: a captions-only connection cannot complete
          // ``videos.insert`` even if the title is set.
          if (!selectedConnectionHasUploadScope) return false;
          return state.target.title.trim().length > 0;
        }
        if (state.target.kind === 'youtube_video') return !!state.target.id;
        return true;
      }
      case 'choose-payload':
        return state.payload.kind === 'captions'
          ? state.payload.languages.length > 0
          : !!state.payload.format;
      case 'confirm':
        return true;
      case 'in-flight':
        return false;
      default:
        return false;
    }
  }, [selectedConnectionHasUploadScope, state]);

  const canUseConnections = connections.length > 0;

  return {
    state,
    connections,
    connectionsLoading,
    canUseConnections,
    selectedConnection,
    youtubeUploadsEnabled,
    publicPrivacyAvailable,
    selectedConnectionHasUploadScope,
    targets: flattenedTargets,
    targetsLoading,
    targetsHasNextPage: !!targetsHasNextPage,
    targetsIsFetchingNextPage,
    targetsFetchNextPage,
    targetQuery,
    setTargetQuery,
    priorAttempt,
    canAdvance,
    chooseConnection,
    pickTarget,
    setPayload,
    confirmPublish,
    retryPublish,
    pickDifferentTarget,
    cancelActiveJob,
    isCancellingJob,
    goTo,
    setActiveJobId
  } as const;
}
